/**
 * Order-to-cash helpers, client side (OS-0672, OS-0673).
 *
 * The authority is `onesphere/engines/orders.py`: `collect` re-runs `validate_receipt` and refuses the receipt, so
 * nothing here can be bypassed. The mirror exists so the finance clerk is told *which field* is missing before the
 * button is pressed — the same codes, rendered by `o2c.receiptProblem.<code>`.
 */

export type ReceiptDraft = {
  /** Only a partial receipt carries an amount; the full receipt takes what the invoice still owes. */
  amount?: string;
  outstanding: number;
  account: string;
  /** `Bank` or `Cash` — the type of the account chosen. */
  accountType?: string;
  referenceNo: string;
  referenceDate: string;
  postingDate: string;
  /** ISO date of today, as the site sees it. */
  today: string;
};

export type ReceiptProblem = { field: "amount" | "paid_to" | "reference_no" | "reference_date" | "posting_date"; code: string };

/** Everything wrong with a receipt the person is about to record — the codes `engines/orders.validate_receipt` uses. */
export function receiptProblems(d: ReceiptDraft): ReceiptProblem[] {
  const out: ReceiptProblem[] = [];
  if (d.amount !== undefined && d.amount !== "") {
    const n = Number(d.amount);
    if (Number.isNaN(n)) out.push({ field: "amount", code: "not_a_number" });
    else if (n <= 0) out.push({ field: "amount", code: "zero" });
    else if (n > d.outstanding + 0.005) out.push({ field: "amount", code: "over_outstanding" });
  }
  if (!d.account) out.push({ field: "paid_to", code: "required" });
  if (d.postingDate && d.postingDate > d.today) out.push({ field: "posting_date", code: "in_the_future" });
  if (d.referenceDate && d.referenceDate > d.today) out.push({ field: "reference_date", code: "in_the_future" });
  if (d.account && d.accountType === "Bank") {
    if (!d.referenceNo.trim()) out.push({ field: "reference_no", code: "required_for_bank" });
    if (!d.referenceDate) out.push({ field: "reference_date", code: "required_for_bank" });
  }
  return out;
}

/** The work queue, one bucket of it, and optionally one order's trail opened beside it. */
export function queueHref(bucket: string, order?: string | null): string {
  return `/orders?tab=queue&bucket=${encodeURIComponent(bucket)}${order ? `&order=${encodeURIComponent(order)}` : ""}`;
}

/** The *New customer* form, handed back to the screen it was opened from. */
export function newCustomerHref(from: string): string {
  return `/customers/new?from=${encodeURIComponent(from)}`;
}
